import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { CreateFormFeatureDialog } from '../../../../components';
import { GLOSSARY_METADATA_PATHS } from '../../../../constants/glossary-metadata-paths';
import { useImportGlossaryMetadata } from '../../../../hooks/mutateImportGlossaryMetadata';
import {
    FileData,
    replaceModalConfig,
} from '../../../job-manager/components/create-job-dialog';
import JobExecutor from '../../../job-manager/components/job-executor/JobExecutor';
import { glossaryMetadataConfig } from '../../glossary-metadata-manager/create-glossary-metadata/create-glossary-metadata-helper';
import {
    importGlossaryMetadataDefaults,
    ImportGlossaryMetadataFormInputs,
    ImportGlossaryMetadataSchema,
    importGlossaryStyles,
} from './import-glossary-metadata-helper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, usePermission } from '../../../../core';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { Grid, Typography } from '@mui/material';
import { DragAndDrop } from '@biq/apex-ui';
import { possiblyNavigateToMetadataSearch } from 'lib';

export const ImportGlossaryMetadata = (): React.JSX.Element => {
    const [searchParams] = useSearchParams();
    const { getToken, getUserEmail } = useAuth();
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [files, setFiles] = useState<FileData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fileError, setFileError] = useState<boolean>(false);

    const { mutate: importGlossaryMetadata } = useImportGlossaryMetadata();

    const { handleSubmit } = useForm<ImportGlossaryMetadataFormInputs>({
        mode: 'all',
        resolver: yupResolver(ImportGlossaryMetadataSchema),
        defaultValues: importGlossaryMetadataDefaults,
    });

    const handleImportGlossaryMetadata = (): void => {
        if (files.length === 0) {
            setFileError(true);
        } else {
            setFileError(false);
            setIsLoading(true);
            importGlossaryMetadata(
                {
                    file: files[0],
                    fileType: 'JSON',
                    token: getToken(),
                    email: getUserEmail(),
                },
                {
                    onSuccess: () => {
                        setTimeout(() => {
                            navigate(GLOSSARY_METADATA_PATHS.LANDING_PAGE);
                        }, 500);
                        setIsLoading(false);
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                }
            );
        }
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit(handleImportGlossaryMetadata)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };
    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCloseButton = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${GLOSSARY_METADATA_PATHS.LANDING_PAGE}?option=Import Glossary`,
            searchParams
        );
    };

    const handleBackButton = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${GLOSSARY_METADATA_PATHS.CREATE_NEW_GLOSSARY_METADATA_VIEW}?option=Import Glossary`,
            searchParams
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText={'Start Upload'}
            handleSubmit={onSubmit}
            displaySubmitButton
            isLoading={isLoading}
            handleCloseButton={handleCloseButton}
            handleBackButton={handleBackButton}
            listConfig={glossaryMetadataConfig}
        >
            <Grid sx={importGlossaryStyles.subHeadingTextContainer}>
                <Typography sx={importGlossaryStyles.subHeadingTextStyles}>
                    Glossary file must be a JSON file.
                </Typography>
            </Grid>
            <Grid sx={importGlossaryStyles.gridStyles}>
                <Grid sx={importGlossaryStyles.containerStyles}>
                    <JobExecutor />
                </Grid>
                <Grid sx={importGlossaryStyles.containerStyles}>
                    <Grid>
                        <DragAndDrop
                            files={files}
                            setFiles={setFiles}
                            fileError={fileError}
                            setFileError={setFileError}
                            filePreview={true}
                            showReplaceFileModal={true}
                            replaceModalConfig={replaceModalConfig}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
